import {
  Badge,
  Buzzebees,
  CartCountResponse,
  ConfirmOtpResponse,
  ErrorResponse,
  ExpiringPoints,
  ProfileResponse,
  ServiceResponse,
  StatusResponse,
  Trace,
  UpdatedPoints,
} from '@bzbs/react-api-client';
import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { eventEmitter } from '../service';
import { useAuthContext } from './auth_provider';
import { useBuzzebeesServiceContext } from './buzzebees_service_provider';

type UserContextType = {
  user: ProfileResponse | null;
  points: number;
  recentPoints: number;
  expiringPoints: UpdatedPoints[];
  recentBadge: Badge | null;
  recentTrace: Trace | null;
  cartCount: number;
  isLoading: boolean;
  error: ErrorResponse | null;
  actions: {
    fetchUser: () => Promise<ServiceResponse<ProfileResponse>>;
    fetchPoint: () => Promise<ServiceResponse<UpdatedPoints>>;
    fetchExpiringPoints: () => Promise<ServiceResponse<ExpiringPoints>>;
    fetchCartCount: () => Promise<ServiceResponse<CartCountResponse>>;
    editUser: (data: UpdateProfileParams) => Promise<ServiceResponse<ProfileResponse>>;
    changePassword: (oldPassword: string, newPassword: string) => Promise<ServiceResponse<StatusResponse>>;
    changeContactNumber: (
      contactNumber: string,
      otp: string,
      refCode: string
    ) => Promise<ServiceResponse<ConfirmOtpResponse>>;
    changeAvatar: (avatar: File) => Promise<ServiceResponse<ProfileResponse>>;
    consumeRecentPoints: () => void;
    consumeRecentBadge: () => void;
    consumeRecentTrace: () => void;
    deleteUser: () => Promise<ServiceResponse<any>>;
    setError: (error: ErrorResponse | null) => void;
    clearData: () => void;
  };
};

export type UpdateProfileParams = {
  profileImage?: File | { uri: string; name: string; type: string };
  firstName?: string;
  lastName?: string;
  contactNumber?: string;
  email?: string;
  notification?: boolean;
  locale?: number;
  title?: string;
  gender?: string;
  birthDate?: number;
  address?: string;
  subdistrictCode?: number;
  subdistrictName?: string;
  districtCode?: number;
  districtName?: string;
  provinceCode?: number;
  provinceName?: string;
  countryCode?: string;
  countryName?: string;
  zipCode?: string;
  idCard?: string;
  passport?: string;
  maritalStatus?: string;
  village?: string;
  building?: string;
  number?: string;
  moo?: string;
  room?: string;
  floor?: string;
  soi?: string;
  city?: string;
  road?: string;
  landmark?: string;
  alternateContactNumber?: string;
  homeContactNumber?: string;
  nationality?: string;
  religion?: string;
  location?: string;
  latitude?: number;
  longitude?: number;
  income?: string;
  interests?: string;
  region?: string;
  phonepurchase?: number;
  highestEducation?: string;
  occupation?: string;
  remark?: string;
  displayName?: string;
  options?: { [key: string]: any };
};

export const UserContext = createContext<UserContextType>({
  user: null,
  points: 0,
  expiringPoints: [] as UpdatedPoints[],
  recentPoints: 0,
  recentBadge: null,
  recentTrace: null,
  cartCount: 0,
  isLoading: false,
  error: null,
  actions: {
    fetchUser: async () => ({ type: 'client-error', message: 'Not implemented' }),
    fetchPoint: async () => ({ type: 'client-error', message: 'Not implemented' }),
    fetchExpiringPoints: async () => ({ type: 'client-error', message: 'Not implemented' }),
    fetchCartCount: async () => ({ type: 'client-error', message: 'Not implemented' }),
    editUser: async () => ({ type: 'client-error', message: 'Not implemented' }),
    changePassword: async () => ({ type: 'client-error', message: 'Not implemented' }),
    changeContactNumber: async () => ({ type: 'client-error', message: 'Not implemented' }),
    changeAvatar: async () => ({ type: 'client-error', message: 'Not implemented' }),
    consumeRecentPoints: () => {},
    consumeRecentBadge: () => {},
    consumeRecentTrace: () => {},
    deleteUser: async () => ({ type: 'client-error', message: 'Not implemented' }),
    setError: () => {},
    clearData: () => {},
  } as UserContextType['actions'],
});

export function useUserContext() {
  return useContext(UserContext);
}

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<ProfileResponse | null>(null);
  const [points, setPoints] = useState<number>(0);
  const [expiringPoints, setExpiringPoints] = useState<UpdatedPoints[]>([]);
  const [cartCount, setCartCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>(null);

  const [recentPoints, setRecentPoints] = useState<number>(0);
  const [recentBadge, setRecentBadge] = useState<Badge | null>(null);
  const [badgeList, setBadgeList] = useState<Badge[]>([]);
  const [recentTrace, setRecentTrace] = useState<Trace | null>(null);
  const [traceList, setTraceList] = useState<Trace[]>([]);

  const { bzbsService } = useBuzzebeesServiceContext();
  const auth = useAuthContext();

  useEffect(() => {
    eventEmitter.on('get_points', (buzzebees: Buzzebees) => {
      console.log('get_points', buzzebees);
      const points = buzzebees.points;
      if (points) {
        setRecentPoints(points);
      }

      const traces = buzzebees.traces;
      if (traces) {
        if (traces.length > 0) {
          const first = traces.shift();
          if (first) {
            setRecentTrace(first);
          }
          setTraceList(traces);
        }
      }
    });

    eventEmitter.on('get_badges', (badges: Badge[]) => {
      if (badges.length > 0) {
        const first = badges.shift();
        if (first) {
          setRecentBadge(first);
        }
        setBadgeList(badges);
      }
    });

    eventEmitter.on('session_expired', () => {
      auth.actions.logout();
    });

    return () => {
      console.log('UserProvider cleanup');
      eventEmitter.clearListeners(['get_points', 'get_badges', 'session_expired']);
    };
  }, []);

  const fetchUser = async (): Promise<ServiceResponse<ProfileResponse>> => {
    setIsLoading(true);
    const response = await bzbsService.profileApi.profile();
    setIsLoading(false);

    if (response.type === 'success') {
      setUser(response.model);
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  const fetchPoint = async (): Promise<ServiceResponse<UpdatedPoints>> => {
    setIsLoading(true);
    const response = await bzbsService.profileApi.points();
    setIsLoading(false);

    if (response.type === 'success') {
      setPoints(response.model.points);
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  const fetchExpiringPoints = async (): Promise<ServiceResponse<ExpiringPoints>> => {
    setIsLoading(true);
    const response = await bzbsService.profileApi.expiringPoints();
    setIsLoading(false);

    if (response.type === 'success') {
      setExpiringPoints(response.model.expiring_points ?? []);
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  const fetchCartCount = async (): Promise<ServiceResponse<CartCountResponse>> => {
    setIsLoading(true);
    const response = await bzbsService.cartApi.cartCount();
    setIsLoading(false);
    if (response.type === 'success') {
      setCartCount(response.model.cart_count ?? 0);
    } else {
      setError(response);
      setCartCount(0);
    }

    return response;
  };

  const editUser = async (data: UpdateProfileParams): Promise<ServiceResponse<ProfileResponse>> => {
    setIsLoading(true);
    const response = await bzbsService.profileApi.updateProfile(data);
    setIsLoading(false);

    if (response.type === 'success') {
      setUser(response.model);
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  const changePassword = async (oldPassword: string, newPassword: string): Promise<ServiceResponse<StatusResponse>> => {
    setIsLoading(true);
    const response = await bzbsService.profileApi.changePassword({ current: oldPassword, change: newPassword });
    setIsLoading(false);

    if (response.type === 'success') {
      setIsLoading(false);
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  const changeContactNumber = async (
    contactNumber: string,
    otp: string,
    refCode: string
  ): Promise<ServiceResponse<ConfirmOtpResponse>> => {
    setIsLoading(true);
    const response = await bzbsService.profileApi.changeContactNumber({ contactNumber, otp: otp, refCode });
    setIsLoading(false);

    if (response.type === 'success') {
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  const changeAvatar = async (avatar: File): Promise<ServiceResponse<ProfileResponse>> => {
    setIsLoading(true);
    const response = await bzbsService.profileApi.updateProfile({ profileImage: avatar });
    setIsLoading(false);

    if (response.type === 'success') {
      setUser(response.model);
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  const consumeRecentPoints = () => {
    setRecentPoints(0);
  };

  const consumeRecentBadge = () => {
    setRecentBadge(null);
    const next = badgeList.shift();
    if (next) {
      setRecentBadge(next);
      setBadgeList(badgeList);
    }
  };

  const consumeRecentTrace = () => {
    setRecentTrace(null);
    const next = traceList.shift();
    if (next) {
      setRecentTrace(next);
      setTraceList(traceList);
    }
  };

  const deleteUser = async (): Promise<ServiceResponse<any>> => {
    setIsLoading(true);
    const response = await bzbsService.profileApi.deactivate();
    setIsLoading(false);

    if (response.type === 'success') {
      clearData();
    } else {
      setError(response);
    }

    return response;
  };

  const clearData = () => {
    setUser(null);
    setPoints(0);
    setExpiringPoints([]);
    setError(null);
  };

  return (
    <UserContext.Provider
      value={{
        user,
        points,
        expiringPoints,
        recentPoints,
        recentBadge,
        recentTrace,
        cartCount,
        isLoading,
        error,
        actions: {
          fetchUser,
          fetchPoint,
          fetchExpiringPoints,
          fetchCartCount,
          editUser,
          changePassword,
          changeContactNumber,
          changeAvatar,
          consumeRecentPoints,
          consumeRecentBadge,
          consumeRecentTrace,
          deleteUser,
          setError,
          clearData,
        },
      }}
    >
      {children}
    </UserContext.Provider>
  );
}
